import { SearchUI } from '@/components/golf/SearchUI';
import { IMAGES_BASE_URL } from '@/lib/config';
import { isDebugMode } from '@/lib/utils';
import { getFavoriteGolfClubs, type Course } from '@/services/golfClubService';
import { api } from '@/utils/api';
import { getGeolocationWithCache } from '@/utils/geolocation';
import { getRecentlyVisitedClubs, saveVisitedClub } from '@/utils/recentClubs';
import { IonButton, IonModal, IonPage } from '@ionic/react';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import { Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GolfCourse } from './game-setup';
import './GolfClubSearch.css';
import CourseSelectionPage from './search/CourseSelectionPage';
import { ImageWithFallback } from './ui/fallbackImage';

type GolfClubSearchProps = {
  onCourseSelect?: (course: Course, selectedCourse?: GolfCourse) => void;
  mode?: 'Search' | 'Select';
  customSearchCaption?: string;
  iconOnly?: boolean;
  initialValue?: Course;
  className?: string;
  inline?: boolean; // If true, renders inline instead of modal
  selectCourse?: boolean; // If true, shows course selection when multiple courses are available
  noTabs?: boolean; // If true, hides the tabs
  backHandler?: () => boolean; // Optional callback for back button
};

export default function GolfClubSearch({
  onCourseSelect,
  mode = 'Select',
  customSearchCaption,
  initialValue,
  className,
  inline = false,
  selectCourse = false,
  noTabs = false,
  backHandler
}: GolfClubSearchProps) {
  const { t } = useTranslation('MGP');

  const [open, setOpen] = useState(inline);
  const [courses, setCourses] = useState<Course[]>([]);

  const [nearestCourses, setNearestCourses] = useState<Course[]>(() => {
    // Try to load nearest courses from localStorage
    try {
      const cachedData = localStorage.getItem('nearestCourses');
      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        // Check if the cache is still valid (30 minutes = 1800000 ms)
        if (Date.now() - timestamp < 1800000) {
          return data;
        } else {
          localStorage.removeItem('nearestCourses');
        }
      }
    } catch (error) {
      isDebugMode() && console.error('Error loading cached nearest courses:', error);
      localStorage.removeItem('nearestCourses');
    }
    return [];
  });
  const [playedCourses, setPlayedCourses] = useState<Course[]>([]);
  const [favoriteCourses, setFavoriteCourses] = useState<Course[]>([]);
  const [value, setValue] = useState<Course | null>(initialValue || null);
  const [isSearching, setIsSearching] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTouched, setSearchTouched] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [segment, setSegment] = useState<'nearest' | 'playedCourses' | 'favorites'>('nearest');
  const [selectedClub, setSelectedClub] = useState<Course | null>(null);
  const [showCourseSelection, setShowCourseSelection] = useState(false);
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);

  useEffect(() => {
    if (location) {
      fetchCoursesForSegment('nearest');
    }
  }, [location]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (inline) return; // No modal open/close in inline mode
    setOpen(isOpen);
    if (isOpen && location === null) getGeolocation();
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  };

  // Handle segment change
  const handleSegmentChange = (value: 'nearest' | 'playedCourses' | 'favorites') => {
    setSegment(value);

    // Reset search when changing segments
    setInputValue('');

    // Immediately show what we have in state for the selected segment
    if (value === 'nearest' && nearestCourses.length > 0) {
      setCourses(nearestCourses);
    } else if (value === 'playedCourses' && playedCourses.length > 0) {
      setCourses(playedCourses);
    } else if (value === 'favorites' && favoriteCourses.length > 0) {
      setCourses(favoriteCourses);
    } else {
      // Fetch courses for the selected segment if not already loaded
      setCourses([]);
      fetchCoursesForSegment(value);
    }
  };


  const getGeolocation = async () => {
    try {
      // Use the cached geolocation utility
      const locationData = await getGeolocationWithCache();
      setLocation(locationData);
    } catch (error) {
      console.error('Error getting location:', error);
    }
  };

  const fetchCourses = async (query: string, segmentValue: 'nearest' | 'playedCourses' | 'favorites' = segment) => {
    try {
      // For 'playedCourses' segment, get recently visited clubs from local storage
      if (segmentValue === 'playedCourses' && !query) {
        const recentClubs = await getRecentlyVisitedClubs();
        // Convert RecentGolfClub to Course type
        return recentClubs.map(club => ({
          id: club.id,
          name: club.name,
          slug: club.slug,
          city: club.city,
          country: club.country,
          distance: club.distance || 0,
          holes: club.holes || 0,
          latlon: null,
          thumbnail: '',
          courses: []
        }));
      }

      // For favorites segment, get favorite clubs from our service
      if (segmentValue === 'favorites' && !query) {
        const favoriteClubs = await getFavoriteGolfClubs();
        return favoriteClubs;
      }

      // For other segments or when there's a search query, use the API
      let searchParams = query;
      if (location !== null) {
        const locationParams = `latitude=${encodeURIComponent(location.latitude)}&longitude=${encodeURIComponent(location.longitude)}`;
        searchParams = searchParams ? `${searchParams}&${locationParams}` : locationParams;
      }

      // Request courses data if course selection is enabled
      if (selectCourse) {
        searchParams = searchParams ? `${searchParams}&withCourses=true` : `withCourses=true`;
      }

      // Make the API call to fetch golf clubs
      const { data } = await api.get(`/golf-clubs?${searchParams}`);
      if (!data) throw new Error('No data received from API');
      return data;
    } catch (error) {
      console.error('Error fetching courses:', error);
      return [];
    }
  };

  const fetchCoursesForSegment = useCallback(async (segmentValue: 'nearest' | 'playedCourses' | 'favorites' = segment) => {
    if (!location && segmentValue === 'nearest') {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      if (segmentValue === 'nearest' && location) {
        // Only fetch nearest courses if we don't already have them
        if (nearestCourses.length === 0) {
          const data = await fetchCourses('', segmentValue);
          // Save to state
          setNearestCourses(data);
          setCourses(data);

          // Save to localStorage with timestamp for 30-minute cache
          try {
            localStorage.setItem('nearestCourses', JSON.stringify({
              data,
              timestamp: Date.now()
            }));
          } catch (error) {
            isDebugMode() && console.error('Error caching nearest courses:', error);
          }
        } else {
          if (segmentValue === segment) {
            setCourses(nearestCourses);
          }
        }
      } else if (segmentValue === 'playedCourses') {
        const recentClubs = await getRecentlyVisitedClubs();
        if (recentClubs.length > 0) {
          // Convert RecentGolfClub to Course type
          const playedCoursesData: Course[] = recentClubs.map(club => ({
            id: parseInt(club.id, 10),  // Convert string id to number
            name: club.name,
            city: club.city,
            country: club.country,
            distance: club.distance || 0,
            latlon: null,
            thumbnail: '',
            courses: club.holes ? [{
              id: 0,
              name: '',
              holes: club.holes,
              courseId: '0',
              courseName: '',
              city: club.city,
              country: club.country,
              distance: 0,
              thumbnail: '',
              latlon: { x: 0, y: 0 },
              facilityId: '0',
              slug: '',
              par: 72,
              courseType: '',
              guestPolicy: ''
            }] : undefined
          }));
          // Update both the playedCourses state and the main courses state
          setPlayedCourses(playedCoursesData);

          // Only update the main courses state if we're currently on the playedCourses segment
          if (segmentValue === segment) {
            setCourses(playedCoursesData);
          }
        }
      } else if (segmentValue === 'favorites') {
        const favorites = await getFavoriteGolfClubs();
        if (favorites.length > 0) {
          // Convert GolfClubService.Course to Course type
          const favoritesData: Course[] = favorites.map(club => ({
            id: typeof club.id === 'string' ? parseInt(club.id, 10) : club.id,
            name: club.name,
            city: club.city,
            country: club.country,
            distance: club.distance || 0,
            latlon: typeof club.latlon === 'string' ? { x: 0, y: 0 } : (club.latlon || { x: 0, y: 0 }),
            thumbnail: club.thumbnail || '',
            courses: Array.isArray(club.courses) ? club.courses.map(c => ({
              id: typeof c.id === 'number' ? c.id : 0,
              name: c.name || '',
              holes: c.holes || 18,
              courseId: c.courseId || '0',
              courseName: c.courseName || '',
              city: club.city,
              country: club.country,
              distance: 0,
              thumbnail: '',
              latlon: { x: 0, y: 0 },
              facilityId: c.facilityId || '0',
              par: c.par || 72,
              courseType: c.courseType || '',
              guestPolicy: c.guestPolicy || ''
            })) : undefined
          }));
          setFavoriteCourses(favoritesData);

          // Only update the main courses state if we're currently on the favorites segment
          if (segmentValue === segment) {
            setCourses(favoritesData);
          }
        }
      }
    } catch (error) {
      console.error(`Error fetching ${segmentValue} courses:`, error);
      // Set empty array to avoid undefined errors
      if (segmentValue === 'favorites') {
        setFavoriteCourses([]);
      }
    } finally {
      setIsSearching(false);
    }
  }, [location, fetchCourses, segment]);



  const handleSearch = useCallback(
    async (value: string) => {
      setIsSearching(true);
      setSearchTouched(true);

      if (value.trim()) {
        const query = `query=${encodeURIComponent(value.trim())}`;
        const data = await fetchCourses(query);
        setCourses(data);
      }

      setIsSearching(false);
    },
    [nearestCourses, playedCourses, favoriteCourses, segment]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      handleSearch(inputValue);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, handleSearch]);

  // Use a ref to track if we've already fetched data in this session
  const hasInitializedRef = useRef(false);

  // Separate function to fetch favorite golf clubs
  const fetchFavoriteClubs = useCallback(async () => {
    try {
      setIsSearching(true);
      const favoriteClubs = await getFavoriteGolfClubs();
      setFavoriteCourses(favoriteClubs);
      if (segment === 'favorites') {
        setCourses(favoriteClubs);
      }
    } catch (error) {
      console.error('Error fetching favorite clubs:', error);
      setFavoriteCourses([]);
    } finally {
      setIsSearching(false);
    }
  }, [segment]);

  useEffect(() => {
    // Only fetch data when the component opens
    if ((inline || open) && !hasInitializedRef.current) {
      // Mark as initialized to prevent duplicate fetches
      hasInitializedRef.current = true;
      getGeolocation();
      // Fetch favorite clubs using our dedicated function
      fetchFavoriteClubs();
    }

    // Reset the initialization flag when the component closes
    if (!open && !inline) {
      hasInitializedRef.current = false;
    }
  }, [open, inline, fetchFavoriteClubs]);
  // Map functionality has been moved to the SearchMap component

  const handleSelectCourse = (courseOrSelectedCourse: Course | any) => {
    // Check if this is a course from the CourseSelectionPage
    if (courseOrSelectedCourse.courseId) {
      // This is a selected course from CourseSelectionPage
      const selectedCourse = courseOrSelectedCourse;

      if (selectedClub && onCourseSelect) {
        onCourseSelect(selectedClub, selectedCourse);
        setOpen(false);
        setShowCourseSelection(false);
        setValue(selectedClub);
      }
      return;
    }

    // This is a club/course from the main list
    const course = courseOrSelectedCourse as Course;

    // Save the selected course to recently visited clubs
    saveVisitedClub({
      id: String(course.id),
      name: course.name,
      slug: course.slug,
      city: course.city,
      country: course.country,
      distance: course.distance,
      holes: course.courses && course.courses.length > 0 ? course.courses[0].holes : 18
    });

    // If the course has multiple courses and selectCourse is true, show the course selection
    if (selectCourse && course.courses && course.courses.length > 1) {
      setSelectedClub(course);
      setShowCourseSelection(true);
      return;
    }

    // If there's only one course or selectCourse is false, select it directly
    const selectedCourse = course.courses && course.courses.length === 1 ? course.courses[0] : undefined;

    if (onCourseSelect) {
      onCourseSelect(course, selectedCourse);
      setOpen(false);
    }
    setValue(course);
  };

  const handleLocateUser = () => {
    if (!mapRef.current) return;
    if (!location) return;

    mapRef.current.flyTo({
      center: [location.longitude, location.latitude],
      zoom: 13
    });
  };

  // Handle back from course selection
  const handleBackFromCourseSelection = () => {
    setShowCourseSelection(false);
    setSelectedClub(null);
  };

  // Decide which UI to render based on inline prop
  if (inline) {
    // Inline mode: render search UI directly
    return (
      <IonPage className={`${className || ''} flex flex-col h-full`}>
        {/* Content section: conditionally render UI */}
        {showCourseSelection && selectedClub ? (
          <CourseSelectionPage
            club={selectedClub}
            onBack={handleBackFromCourseSelection}
            onSelectCourse={handleSelectCourse}
          />
        ) : (
          <SearchUI
            showMap={showMap}
            setShowMap={setShowMap}
            searchRef={searchRef}
            inputValue={inputValue}
            setInputValue={setInputValue}
            segment={segment}
            handleSegmentChange={handleSegmentChange}
            isSearching={isSearching}
            courses={courses}
            playedCourses={playedCourses}
            favoriteCourses={favoriteCourses}
            handleSelectCourse={handleSelectCourse}
            searchTouched={searchTouched}
            location={location}
            getGeolocation={getGeolocation}
            mapContainerRef={mapContainerRef}
            handleLocateUser={handleLocateUser}
            selectCourse={selectCourse}
            customSearchCaption={customSearchCaption}
            backHandler={() => setOpen(false)}
            noTabs={noTabs}
          />
        )}
      </IonPage>
    );
  }

  // Modal mode (default)
  return (
    <>
      <IonButton
        type="button"
        color="transparent"
        role="combobox"
        onClick={() => handleOpenChange(!open)}
        className={`w-full text-foreground justify-start text-start hover:bg-transparent font-normal ${className}`}
      >
        {mode === 'Select' && value && (
          <ImageWithFallback
            key={`club-image-${value?.id}`}
            alt=''
            fallbackImage="/assets/images/fallback.jpg"
            src={`${IMAGES_BASE_URL}/${value?.id}.jpg`}
            className="size-10 shrink-0 overflow-hidden object-cover rounded mr-2"
          />
        )}
        {value && mode === 'Select' ? (
          <span className="flex items-center font-semibold gap-3 w-full">
            {value.name}
          </span>
        ) : (
          mode === 'Select' ? (
            <span className="flex items-center justify-between gap-1 w-full font-normal">
              <span>{customSearchCaption || t('search_golf_course_placeholder')}</span>
              <ChevronRightIcon className="size-5" />
            </span>
          ) : (
            <span className="flex items-center gap-1 w-full">
              <Search className="size-5" />
              <span className='opacity-50'>{customSearchCaption || t('search_golf_course_placeholder')}</span>
            </span>
          )
        )}
      </IonButton >
      <IonModal isOpen={open} onDidDismiss={() => handleOpenChange(false)} className='golfClubSearchModal'>
        <SearchUI
          backHandler={() => handleOpenChange(false)}
          showMap={showMap}
          setShowMap={setShowMap}
          searchRef={searchRef}
          inputValue={inputValue}
          setInputValue={setInputValue}
          segment={segment}
          handleSegmentChange={handleSegmentChange}
          isSearching={isSearching}
          courses={courses}
          playedCourses={playedCourses}
          favoriteCourses={favoriteCourses}
          handleSelectCourse={handleSelectCourse}
          searchTouched={searchTouched}
          location={location}
          getGeolocation={getGeolocation}
          mapContainerRef={mapContainerRef}
          handleLocateUser={handleLocateUser}
          selectCourse={selectCourse}
          customSearchCaption={customSearchCaption}
          noTabs={noTabs}
        />
      </IonModal >
    </>
  );
}